import { useState, useEffect, useCallback } from "react";
import "./css/adminpayoutfee.css";
import { apiGet, apiPut, apiDelete } from "../../utils/apiClient";

const EASY_PAY_COLUMNS = [
  { key: "toss", label: "토스페이" },
  { key: "payco", label: "페이코" },
  { key: "kakao", label: "카카오페이" },
  { key: "samsung", label: "삼성페이" },
  { key: "naver", label: "네이버페이" },
];

const CREDIT_TYPE_KEY_MAP = {
  토스페이: "toss",
  페이코: "payco",
  카카오페이: "kakao",
  삼성페이: "samsung",
  네이버페이: "naver",
};

const EASY_PAY_ROWS = [
  { label: "카드", paymentType: "간편결제 카드" },
  { label: "포인트/머니", paymentType: "간편결제 포인트" },
];

// 신용카드/체크카드는 화면상 "전체" 한 컬럼으로 합쳐 보여주지만, 실제로는 두 행(PAYMENT_TYPE)이 같은 값을 유지해야 해서
// creditType(은행)별로 그룹을 묶어 CREDIT_CARD/DEBIT_CARD 행을 항상 함께 조회/수정/삭제한다.
// "알 수 없음"(UNKNOWN)은 은행 구분 없는 기본값이라 항상 첫 번째("기본") 행으로 고정한다.
const buildCardGroups = (paymentFees) => {
  const fees = paymentFees.filter(
    (fee) =>
      fee.installmentMonth === 0 &&
      (fee.paymentType === "신용카드" || fee.paymentType === "체크카드"),
  );
  const byBank = new Map();
  fees.forEach((fee) => {
    if (!byBank.has(fee.creditType)) byBank.set(fee.creditType, []);
    byBank.get(fee.creditType).push(fee);
  });

  return Array.from(byBank.entries())
    .sort(([a], [b]) => (a === "알 수 없음" ? -1 : b === "알 수 없음" ? 1 : a.localeCompare(b)))
    .map(([creditType, groupFees]) => ({
      key: `card-${creditType}`,
      creditType,
      label: creditType === "알 수 없음" ? "기본" : creditType,
      fees: groupFees,
    }));
};

// 할부 표도 마찬가지로 은행별로 묶되, 은행 하나당 2~12개월 11개 행이 필요하므로 (은행 -> 개월) 2단 구조로 그룹핑한다.
const buildInstallmentBankGroups = (paymentFees) => {
  const fees = paymentFees.filter((fee) => fee.installmentMonth > 0);
  const byBank = new Map();
  fees.forEach((fee) => {
    if (!byBank.has(fee.creditType)) byBank.set(fee.creditType, []);
    byBank.get(fee.creditType).push(fee);
  });

  return Array.from(byBank.entries())
    .sort(([a], [b]) => (a === "알 수 없음" ? -1 : b === "알 수 없음" ? 1 : a.localeCompare(b)))
    .map(([creditType, bankFees]) => {
      const byMonth = new Map();
      bankFees.forEach((fee) => {
        if (!byMonth.has(fee.installmentMonth)) byMonth.set(fee.installmentMonth, []);
        byMonth.get(fee.installmentMonth).push(fee);
      });
      const monthGroups = Array.from(byMonth.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([month, monthFees]) => ({
          key: `installment-${creditType}-${month}`,
          month,
          fees: monthFees,
        }));

      return {
        creditType,
        label: creditType === "알 수 없음" ? "기본" : creditType,
        monthGroups,
      };
    });
};

const AdminPayoutFee = () => {
  const [paymentFees, setPaymentFees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingSection, setEditingSection] = useState({ card: false, easypay: false, installment: false });
  const [draftValues, setDraftValues] = useState({});

  const fetchPaymentFees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet("/admin/paymentfee");
      if (response.ok) {
        const data = await response.json();
        setPaymentFees(data || []);
      } else {
        setError("수수료 정보 조회에 실패했습니다.");
      }
    } catch (err) {
      console.error("수수료 정보 조회 실패:", err);
      setError("수수료 정보 조회 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentFees();
  }, [fetchPaymentFees]);

  // 신용·체크카드 (기본 + 은행별 예외 요율)
  const cardGroups = buildCardGroups(paymentFees);

  // 간편결제 매트릭스
  const easyPayFees = paymentFees.filter((fee) => fee.installmentMonth === 0 && fee.creditType !== "알 수 없음");

  // 신용·체크카드 무이자 할부 (기본 + 은행별 예외 요율)
  const installmentBankGroups = buildInstallmentBankGroups(paymentFees);

  // 간편결제 매트릭스의 각 셀을 그룹 형태로 미리 계산 (결제수단 x PG사)
  const easyPayGroups = EASY_PAY_ROWS.map((row) => {
    const rowFees = easyPayFees.filter((fee) => fee.paymentType === row.paymentType);
    const feeByKey = {};
    rowFees.forEach((fee) => {
      const key = CREDIT_TYPE_KEY_MAP[fee.creditType];
      if (key) feeByKey[key] = fee;
    });
    return {
      ...row,
      cells: EASY_PAY_COLUMNS.map((col) => ({
        key: `easypay-${row.paymentType}-${col.key}`,
        col,
        fees: feeByKey[col.key] ? [feeByKey[col.key]] : [],
      })),
    };
  });

  const sectionGroups = {
    card: cardGroups,
    easypay: easyPayGroups.flatMap((row) => row.cells).filter((cell) => cell.fees.length > 0),
    installment: installmentBankGroups.flatMap((bank) => bank.monthGroups),
  };

  const handleEditStart = (section) => {
    const seed = {};
    sectionGroups[section].forEach((group) => {
      seed[group.key] = String(group.fees[0].rateValue);
    });
    setDraftValues((prev) => ({ ...prev, ...seed }));
    setEditingSection((prev) => ({ ...prev, [section]: true }));
  };

  const handleEditCancel = (section) => {
    setDraftValues((prev) => {
      const next = { ...prev };
      sectionGroups[section].forEach((group) => {
        delete next[group.key];
      });
      return next;
    });
    setEditingSection((prev) => ({ ...prev, [section]: false }));
  };

  const handleDraftChange = (groupKey, value) => {
    setDraftValues((prev) => ({ ...prev, [groupKey]: value }));
  };

  const handleEditSave = async (section) => {
    const groups = sectionGroups[section];
    const changed = groups.filter((group) => {
      const draft = draftValues[group.key];
      return draft !== undefined && draft !== "" && Number(draft) !== group.fees[0].rateValue;
    });

    if (changed.some((group) => Number.isNaN(Number(draftValues[group.key])))) {
      alert("올바른 숫자를 입력해주세요.");
      return;
    }

    if (changed.length === 0) {
      handleEditCancel(section);
      return;
    }

    try {
      const responses = await Promise.all(
        changed.flatMap((group) =>
          group.fees.map((fee) =>
            apiPut(`/admin/paymentfee/${fee.paymentFeeIdx}`, { rateValue: Number(draftValues[group.key]) }),
          ),
        ),
      );

      if (responses.every((res) => res.ok)) {
        alert("수수료가 성공적으로 수정되었습니다.");
        handleEditCancel(section);
        await fetchPaymentFees();
      } else {
        alert("수수료 수정에 실패했습니다.");
      }
    } catch (err) {
      console.error("수수료 수정 실패:", err);
      alert("수수료 수정 중 오류가 발생했습니다.");
    }
  };

  const handleDeleteCell = async (group) => {
    if (!window.confirm("이 항목을 삭제하시겠습니까?")) return;

    try {
      const responses = await Promise.all(
        group.fees.map((fee) => apiDelete(`/admin/paymentfee/${fee.paymentFeeIdx}`)),
      );

      if (responses.every((res) => res.ok)) {
        setDraftValues((prev) => {
          const next = { ...prev };
          delete next[group.key];
          return next;
        });
        await fetchPaymentFees();
      } else {
        alert("삭제에 실패했습니다.");
      }
    } catch (err) {
      console.error("수수료 삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  if (loading) {
    return (
      <div className="adminpayoutfee-container">
        <div className="adminpayoutfee-wrap">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="adminpayoutfee-container">
        <div className="adminpayoutfee-wrap">{error}</div>
      </div>
    );
  }

  const renderSectionActions = (section) =>
    editingSection[section] ? (
      <div className="adminpayoutfee-section-actions">
        <button className="adminpayoutfee-btn" onClick={() => handleEditCancel(section)}>
          취소
        </button>
        <button className="adminpayoutfee-btn primary" onClick={() => handleEditSave(section)}>
          저장
        </button>
      </div>
    ) : (
      <div className="adminpayoutfee-section-actions">
        <button className="adminpayoutfee-btn" onClick={() => handleEditStart(section)}>
          수정
        </button>
      </div>
    );

  const renderCell = (group, section) => {
    if (!group || group.fees.length === 0) return null;
    if (editingSection[section]) {
      return (
        <span className="adminpayoutfee-input-wrap">
          <input
            type="number"
            step="0.01"
            className="adminpayoutfee-input"
            value={draftValues[group.key] ?? ""}
            onChange={(e) => handleDraftChange(group.key, e.target.value)}
          />
          <button
            type="button"
            className="adminpayoutfee-delete-btn"
            onClick={() => handleDeleteCell(group)}
            title="삭제"
          >
            ×
          </button>
        </span>
      );
    }
    return `${group.fees[0].rateValue}%`;
  };

  return (
    <div className="adminpayoutfee-container">
      <div className="adminpayoutfee-wrap">
        {/* 신용·체크카드 */}
        <div className="adminpayoutfee-section">
          <div className="adminpayoutfee-section-header">
            <h2 className="adminpayoutfee-section-title">신용·체크카드</h2>
            {renderSectionActions("card")}
          </div>
          <table className="adminpayoutfee-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }} />
                <th>전체</th>
              </tr>
            </thead>
            <tbody>
              {cardGroups.map((group) => (
                <tr key={group.key}>
                  <td className="adminpayoutfee-row-label">{group.label}</td>
                  <td>{renderCell(group, "card")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 간편결제 */}
        <div className="adminpayoutfee-section">
          <div className="adminpayoutfee-section-header">
            <h2 className="adminpayoutfee-section-title">간편결제</h2>
            {renderSectionActions("easypay")}
          </div>
          <table className="adminpayoutfee-table">
            <thead>
              <tr>
                <th style={{ width: "20%" }} />
                {EASY_PAY_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {easyPayGroups.map((row) => (
                <tr key={row.label}>
                  <td className="adminpayoutfee-row-label">{row.label}</td>
                  {row.cells.map((cell) => (
                    <td key={cell.key}>{renderCell(cell, "easypay")}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="adminpayoutfee-note">
            별도 설정되지 않은 간편결제 수수료는{" "}
            <span className="adminpayoutfee-note-highlight">신용·체크카드</span>{" "}
            수수료를 따라가요.
          </p>
        </div>

        {/* 신용·체크카드 무이자 할부 */}
        <div className="adminpayoutfee-section">
          <div className="adminpayoutfee-section-header">
            <h2 className="adminpayoutfee-section-title">신용·체크카드 무이자 할부</h2>
            {renderSectionActions("installment")}
          </div>
          {installmentBankGroups.map((bank) => (
            <table key={bank.creditType} className="adminpayoutfee-table adminpayoutfee-table-installment">
              <thead>
                <tr>
                  <th style={{ width: "20%" }} />
                  <th>{bank.label === "기본" ? "전체" : bank.label}</th>
                </tr>
              </thead>
              <tbody>
                {bank.monthGroups.map((group) => (
                  <tr key={group.key}>
                    <td className="adminpayoutfee-row-label">{group.month}개월</td>
                    <td>{renderCell(group, "installment")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPayoutFee;
